import {
  boundingRect,
  descendantIds,
  elementBox,
  type Rect,
  rectsIntersect,
} from "../scene/geometry";
import { scaleContent } from "../scene/ops";
import type { FormatPreset } from "../scene/presets";
import type { OverlayBox, SceneDocument, SceneElement } from "../scene/schema";
import { defaultCaptionBox, type FrameSize } from "./frame";
import {
  equivalentSceneLayout,
  type LayoutSlotBox,
  SCENE_LAYOUT_IDS,
  type SceneLayoutId,
  sceneLayoutBoxes,
} from "./layouts";
import { scaleCaptionStyle } from "./presets";

/**
 * "Format ändern" of a video document (docs/05-video-und-untertitel.md
 * "Formate"): changes the frame in place, so undo and live collaboration
 * treat it like any other edit.
 *
 * - Sizes follow the shorter side (9:16 → 16:9 keeps text and logos at the
 *   same size), but nothing gets wider or taller than fits the new frame.
 * - Elements that overlap and are visible at the same time (a text on its
 *   box, a badge on a card) move as one, so they stay together.
 * - Positions are proportional: whatever sat near an edge keeps its relative
 *   distance to that edge, things in the middle stay in the middle.
 * - Backgrounds and bars spanning the frame are stretched along that axis.
 * - Captions keep their style and go where the new format expects them
 *   (unless they were moved away from the default place).
 * - Clip boxes of screen layouts become the same layout in the new format
 *   (a split screen stays a split screen, side by side in landscape); other
 *   boxes (picture in picture) keep their aspect ratio. Clips keep their
 *   framing mode, so "cover" fills the new frame again.
 */
export function changeVideoFormat(
  doc: SceneDocument,
  target: Pick<FormatPreset, "id" | "width" | "height">,
): void {
  const from: FrameSize = { width: doc.format.width, height: doc.format.height };
  const to: FrameSize = { width: target.width, height: target.height };
  const resized = from.width !== to.width || from.height !== to.height;
  if (resized) {
    const boxes = [
      ...(doc.timeline?.clips ?? []).flatMap((clip) => (clip.box ? [clip.box] : [])),
      ...(doc.timeline?.overlays ?? []).map((clip) => clip.box),
    ];
    const mapper = new FrameMapper(from, to, boxes);
    relayoutElements(doc, mapper);
    const timeline = doc.timeline;
    for (const clip of timeline?.clips ?? []) {
      if (!clip.box) continue;
      const mapped = mapper.clipBox(clip.box, clip.radius ?? 0);
      if (mapped.full) {
        delete clip.box;
        delete clip.radius;
      } else {
        clip.box = mapped.box;
        if (mapped.radius > 0) clip.radius = mapped.radius;
        else delete clip.radius;
      }
    }
    for (const clip of timeline?.overlays ?? []) {
      const mapped = mapper.clipBox(clip.box, clip.radius);
      clip.box = mapped.box;
      clip.radius = mapped.radius;
    }
  }
  doc.format = { preset: target.id, width: to.width, height: to.height, print: null };
}

/** Near the frame edges: counts as spanning the frame. */
const TOLERANCE = 0.04;
/** Most of the new frame a scaled group of elements may take (unless it was bigger before). */
const MAX_SHARE = 0.92;

type Axis = "x" | "y";

class FrameMapper {
  readonly sx: number;
  readonly sy: number;
  /** Scale of sizes: ratio of the shorter sides. */
  readonly s: number;
  private readonly slots: { id: SceneLayoutId; spaced: boolean; slots: LayoutSlotBox[] }[];

  constructor(
    readonly from: FrameSize,
    readonly to: FrameSize,
    /** Clip boxes of the document: layouts matching more of them are checked first. */
    boxes: readonly OverlayBox[] = [],
  ) {
    this.sx = to.width / from.width;
    this.sy = to.height / from.height;
    this.s = Math.min(to.width, to.height) / Math.min(from.width, from.height);
    const candidates = SCENE_LAYOUT_IDS.flatMap((id) =>
      [false, true].map((spaced) => ({
        id,
        spaced,
        slots: sceneLayoutBoxes(id, from, { spaced }),
      })),
    );
    // Some slots are the same in several layouts (the lower quarters of "1 + 2" and "2 × 2").
    const matches = (entry: (typeof candidates)[number]) =>
      boxes.filter((box) => entry.slots.some((slot) => sameBox(slot.box, box))).length;
    this.slots = candidates
      .map((entry, order) => ({ entry, order, score: matches(entry) }))
      .sort((a, b) => b.score - a.score || a.order - b.order)
      .map(({ entry }) => entry);
  }

  size(axis: Axis, frame: FrameSize): number {
    return axis === "x" ? frame.width : frame.height;
  }

  /** Whether a rectangle spans the old frame along an axis. */
  spans(rect: Rect, axis: Axis): boolean {
    const total = this.size(axis, this.from);
    const start = axis === "x" ? rect.x : rect.y;
    const length = axis === "x" ? rect.width : rect.height;
    return start <= total * TOLERANCE && start + length >= total * (1 - TOLERANCE);
  }

  /** Uniform scale of a group of things with this bounding box: shorter side ratio, capped to fit. */
  unitScale(rect: Rect, axes: { x: boolean; y: boolean } = { x: true, y: true }): number {
    let scale = this.s;
    for (const axis of ["x", "y"] as const) {
      if (!axes[axis]) continue;
      const length = axis === "x" ? rect.width : rect.height;
      if (length <= 0) continue;
      const share = Math.max(MAX_SHARE, length / this.size(axis, this.from));
      scale = Math.min(scale, (this.size(axis, this.to) * share) / length);
    }
    return scale;
  }

  /**
   * New start of a span along an axis: near the start or end edge it keeps
   * its relative distance to that edge, in the middle its relative centre.
   * Spans that were inside the frame stay inside.
   */
  place(axis: Axis, start: number, length: number, newLength: number): number {
    const oldTotal = this.size(axis, this.from);
    const newTotal = this.size(axis, this.to);
    const ratio = newTotal / oldTotal;
    const center = (start + length / 2) / oldTotal;
    let next: number;
    if (center < 0.35) next = start * ratio;
    else if (center > 0.65) next = newTotal - (oldTotal - start - length) * ratio - newLength;
    else next = center * newTotal - newLength / 2;
    const inside = start >= -0.5 && start + length <= oldTotal + 0.5;
    if (inside && newLength <= newTotal) next = Math.max(0, Math.min(newTotal - newLength, next));
    return next;
  }

  /** A clip box: the same slot of the same (or the equivalent) screen layout, else proportional. */
  clipBox(box: OverlayBox, radius: number): { box: OverlayBox; radius: number; full: boolean } {
    for (const entry of this.slots) {
      const index = entry.slots.findIndex((slot) => sameBox(slot.box, box));
      if (index < 0) continue;
      const slot = entry.slots[index]!;
      if (Math.abs(slot.radius - radius) > 1) continue;
      const layout = equivalentSceneLayout(entry.id, this.from, this.to);
      const next = sceneLayoutBoxes(layout, this.to, { spaced: entry.spaced })[index];
      if (!next) break;
      return {
        box: { ...next.box },
        radius: next.radius,
        full: next.radius <= 0 && sameBox(next.box, { x: 0, y: 0, ...this.to }),
      };
    }
    const spanX = this.spans(box, "x");
    const spanY = this.spans(box, "y");
    const scale = this.unitScale(box, { x: !spanX, y: !spanY });
    const width = spanX ? box.width * this.sx : box.width * scale;
    const height = spanY ? box.height * this.sy : box.height * scale;
    const next = {
      x: Math.round(spanX ? box.x * this.sx : this.place("x", box.x, box.width, width)),
      y: Math.round(spanY ? box.y * this.sy : this.place("y", box.y, box.height, height)),
      width: Math.max(10, Math.round(width)),
      height: Math.max(10, Math.round(height)),
    };
    return {
      box: next,
      radius: Math.round(radius * (spanX && spanY ? this.s : scale)),
      full: spanX && spanY && radius <= 0 && sameBox(next, { x: 0, y: 0, ...this.to }),
    };
  }
}

function sameBox(a: OverlayBox, b: OverlayBox): boolean {
  return (
    Math.abs(a.x - b.x) <= 1.5 &&
    Math.abs(a.y - b.y) <= 1.5 &&
    Math.abs(a.width - b.width) <= 1.5 &&
    Math.abs(a.height - b.height) <= 1.5
  );
}

interface Item {
  id: string;
  rect: Rect;
  spanX: boolean;
  spanY: boolean;
  start: number;
  end: number;
}

function relayoutElements(doc: SceneDocument, mapper: FrameMapper) {
  for (const page of doc.pages) {
    const items: Item[] = [];
    for (const id of page.children) {
      const element = doc.elements[id];
      if (!element) continue;
      if (element.type === "captions") {
        relayoutCaptions(element, mapper);
        continue;
      }
      const rect = boundingRect(elementBox(element));
      // Like "In anderes Format übertragen": boxes, bars and photos stretch, texts and groups never.
      const stretchable =
        element.type !== "group" &&
        element.type !== "text" &&
        Math.abs((element.rotation ?? 0) % 180) < 0.01;
      items.push({
        id,
        rect,
        spanX: stretchable && mapper.spans(rect, "x"),
        spanY: stretchable && mapper.spans(rect, "y"),
        start: element.time?.start ?? 0,
        end: element.time?.end ?? Number.POSITIVE_INFINITY,
      });
    }
    // Full-frame backgrounds on their own; everything else in groups of overlapping items.
    const units = clusters(
      items.filter((item) => !(item.spanX && item.spanY)),
      Math.min(mapper.from.width, mapper.from.height) * 0.02,
    );
    for (const item of items) {
      if (item.spanX && item.spanY) stretch(doc.elements[item.id]!, mapper);
    }
    for (const unit of units) relayoutUnit(doc, unit, mapper);
  }
}

/**
 * Groups items whose boxes overlap (or nearly touch, closer than `2 × pad`:
 * the lines of a title) while they are visible at the same time.
 */
function clusters(items: Item[], pad: number): Item[][] {
  const grow = (rect: Rect): Rect => ({
    x: rect.x - pad,
    y: rect.y - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  });
  const parent = items.map((_, index) => index);
  const find = (index: number): number => {
    while (parent[index] !== index) {
      parent[index] = parent[parent[index]!]!;
      index = parent[index]!;
    }
    return index;
  };
  for (let a = 0; a < items.length; a++) {
    for (let b = a + 1; b < items.length; b++) {
      const first = items[a]!;
      const second = items[b]!;
      if (first.start >= second.end || second.start >= first.end) continue;
      if (!rectsIntersect(grow(first.rect), grow(second.rect))) continue;
      parent[find(a)] = find(b);
    }
  }
  const groups = new Map<number, Item[]>();
  items.forEach((item, index) => {
    const root = find(index);
    groups.set(root, [...(groups.get(root) ?? []), item]);
  });
  return [...groups.values()];
}

function unionOf(rects: Rect[]): Rect {
  const x = Math.min(...rects.map((rect) => rect.x));
  const y = Math.min(...rects.map((rect) => rect.y));
  const right = Math.max(...rects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height));
  return { x, y, width: right - x, height: bottom - y };
}

/**
 * Moves and scales a group of overlapping elements as one: their common box
 * is placed proportionally, the elements keep their places inside it.
 * Members spanning the frame along an axis stretch along it instead.
 */
function relayoutUnit(doc: SceneDocument, unit: Item[], mapper: FrameMapper) {
  // Along each axis the group is placed by what does not span the frame
  // (a text on a full-width bar keeps its distance to the edge).
  const basis = (axis: Axis) => {
    const own = unit.filter((item) => !(axis === "x" ? item.spanX : item.spanY));
    return unionOf((own.length > 0 ? own : unit).map((item) => item.rect));
  };
  const bx = basis("x");
  const by = basis("y");
  const scale = mapper.unitScale(
    { x: bx.x, y: by.y, width: bx.width, height: by.height },
    { x: !unit.every((item) => item.spanX), y: !unit.every((item) => item.spanY) },
  );
  const x = mapper.place("x", bx.x, bx.width, bx.width * scale);
  const y = mapper.place("y", by.y, by.height, by.height * scale);
  for (const item of unit) {
    const element = doc.elements[item.id]!;
    const before = { x: element.x, y: element.y, width: element.width, height: element.height };
    const oldAspect = element.width / element.height;
    scaleElement(doc, element, scale);
    // Placed by the centre, so rotated elements stay where they were in the group.
    const cx = x + (before.x + before.width / 2 - bx.x) * scale;
    const cy = y + (before.y + before.height / 2 - by.y) * scale;
    element.x = cx - element.width / 2;
    element.y = cy - element.height / 2;
    if (item.spanX) {
      element.width = before.width * mapper.sx;
      element.x = before.x * mapper.sx;
    }
    if (item.spanY) {
      element.height = before.height * mapper.sy;
      element.y = before.y * mapper.sy;
    }
    round(element);
    resetCrop(element, oldAspect);
  }
}

/** Full-frame elements (backgrounds, title cards) keep covering the frame. */
function stretch(element: SceneElement, mapper: FrameMapper) {
  const oldAspect = element.width / element.height;
  element.x *= mapper.sx;
  element.y *= mapper.sy;
  element.width *= mapper.sx;
  element.height *= mapper.sy;
  if (element.type === "shape") {
    element.radius *= mapper.s;
    if (element.stroke) element.stroke.width *= mapper.s;
  }
  if (element.type === "image") {
    element.radius *= mapper.s;
    if (element.stroke) element.stroke.width *= mapper.s;
  }
  round(element);
  resetCrop(element, oldAspect);
}

/** Uniform scale of an element's content; brand-linked text sizes keep the factor. */
function scaleElement(doc: SceneDocument, element: SceneElement, factor: number) {
  if (Math.abs(factor - 1) < 1e-6) return;
  scaleContent(doc, element.id, factor);
  for (const id of [element.id, ...descendantIds(doc, element.id)]) {
    const target = doc.elements[id];
    if (target?.type !== "text" || !target.brand?.text) continue;
    target.textScale = Math.min(10, Math.max(0.1, (target.textScale ?? 1) * factor));
  }
}

/** A changed aspect ratio invalidates a manual crop; "cover" refills the box. */
function resetCrop(element: SceneElement, oldAspect: number) {
  if (
    element.type === "image" &&
    element.crop &&
    Math.abs(element.width / element.height - oldAspect) > 0.01
  ) {
    element.crop = null;
    element.fit = "cover";
  }
}

function round(element: SceneElement) {
  element.x = Math.round(element.x * 10) / 10;
  element.y = Math.round(element.y * 10) / 10;
  element.width = Math.max(1, Math.round(element.width * 10) / 10);
  element.height = Math.max(1, Math.round(element.height * 10) / 10);
}

/**
 * Captions keep their look (sizes follow the shorter side). At their default
 * place they move to the new format's default place; moved elsewhere, they
 * keep their relative position.
 */
function relayoutCaptions(element: SceneElement & { type: "captions" }, mapper: FrameMapper) {
  const { from, to, s } = mapper;
  const before = defaultCaptionBox(from);
  const after = defaultCaptionBox(to);
  scaleCaptionStyle(element.style, s);
  if (element.shadow) {
    element.shadow.x *= s;
    element.shadow.y *= s;
    element.shadow.blur *= s;
  }
  const wide = element.width >= from.width * 0.6;
  const centerY = element.y + element.height / 2;
  const atDefault =
    wide && Math.abs(centerY - (before.y + before.height / 2)) <= from.height * 0.06;
  if (atDefault) {
    Object.assign(element, after);
    return;
  }
  const width = wide ? after.width : Math.min(to.width, element.width * s);
  const height = Math.min(to.height, element.height * s);
  element.x = Math.round(wide ? after.x : mapper.place("x", element.x, element.width, width));
  element.y = Math.round(mapper.place("y", element.y, element.height, height));
  element.width = Math.round(width);
  element.height = Math.round(height);
}
